import { MyUserDataFragment } from '@/generated/codegen';
import { getAppRelease } from '../appRelease';
import { isManagedEmployeeEmail } from '../managedEmployeeEmail';

/**
 * Product analytics identity — the payload sent to PostHog for
 * `posthog.identify(...)` and `posthog.group('company', ...)`.
 *
 * This is the load-bearing safety boundary described in
 * docs/product-analytics-contract.md: `personProperties` is built by
 * EXPLICIT ALLOWLIST construction, field by field. Never spread the user
 * object and delete keys here — a spread-then-delete is how the Smartlook
 * integration (RegisterSmartlookUser.tsx, removed in plan 002) leaked `dni`
 * and `cuil`, and it silently re-leaks whenever a new field is added to the
 * `Me` fragment.
 *
 * When a new PII field is added to `MyUserDataFragment`
 * (packages/frontend/src/modules/app/MyUserSchema.ts), the sentinel list in
 * identity.test.ts must gain a matching entry — that test is the mechanical
 * guarantee this class of bug cannot recur silently.
 */

type CompanyRole = NonNullable<MyUserDataFragment['roles'][number]> & {
  company: NonNullable<
    NonNullable<MyUserDataFragment['roles'][number]>['company']
  >;
};

/**
 * "DEMO - Bilderit" production company id, verified by the maintainer
 * 2026-08-28 (see docs/product-analytics-contract.md). Preview/staging
 * databases are prod-like clones, so the id carries there; environments
 * without a PostHog key never evaluate this anyway. This is a DATA
 * CONVENTION, not an enforced flag — nothing stops the id from differing in
 * some environment, or a second demo company from appearing. A sturdier
 * mechanism (an explicit demo/template flag on `company`) is a recorded
 * backlog item.
 *
 * "Bilderit Templates" was considered for inclusion here but its company id
 * could not be verified anywhere in this repository (no seed data, fixture,
 * or doc reference found) — it is deliberately NOT added rather than
 * guessed. See the executor NOTES for BIL-4823.
 */
export const DEMO_COMPANY_IDS: readonly number[] = [8];

export type AnalyticsIdentity = {
  distinctId: string;
  personProperties: Record<string, string | number | boolean>;
  groupType: 'company';
  groupKey: string | undefined;
  groupProperties: Record<string, string | number | boolean>;
};

const getCompanyRoles = (user: MyUserDataFragment): CompanyRole[] =>
  (user.roles ?? []).filter((role): role is CompanyRole => !!role?.company);

const selectCompanyRole = (
  companyRoles: CompanyRole[],
  currentCompanyId?: number | null
): CompanyRole | undefined => {
  if (currentCompanyId != null) {
    const explicit = companyRoles.find(
      (role) => role.company.id === currentCompanyId
    );
    if (explicit) return explicit;
  }

  const nonDemo = companyRoles.find(
    (role) => !DEMO_COMPANY_IDS.includes(role.company.id)
  );
  if (nonDemo) return nonDemo;

  return companyRoles[0];
};

export const buildAnalyticsIdentity = (
  user: MyUserDataFragment | null | undefined,
  currentCompanyId?: number | null
): AnalyticsIdentity | null => {
  if (!user) return null;

  const companyRoles = getCompanyRoles(user);
  const uniqueCompanyIds = new Set(companyRoles.map((role) => role.company.id));
  const selectedRole = selectCompanyRole(companyRoles, currentCompanyId);
  const appRelease = getAppRelease();

  // Explicit allowlist construction — see the module header comment. Only
  // fields named here are ever sent; never spread `user`.
  const isManaged = isManagedEmployeeEmail(user.email ?? '');
  const personProperties: Record<string, string | number | boolean> = {
    has_multiple_companies: uniqueCompanyIds.size > 1,
    is_managed: isManaged,
  };
  // The email is a person property (never the key), sent so the team
  // (@bilderit.com) can be filtered out of the statistics. Managed employees
  // carry a backend-generated placeholder, not a person's email: skip it.
  if (!isManaged && typeof user.email === 'string' && user.email !== '') {
    personProperties.email = user.email;
  }
  if (import.meta.env.VITE_APP_ENTRY) {
    personProperties.app = import.meta.env.VITE_APP_ENTRY;
  }
  if (appRelease) {
    personProperties.app_version = appRelease;
  }
  if (selectedRole) {
    personProperties.role = selectedRole.role;
    personProperties.company_id = selectedRole.company.id;
    personProperties.company_slug = selectedRole.company.slug;
  }

  const groupProperties: Record<string, string | number | boolean> =
    selectedRole
      ? {
          name: selectedRole.company.name,
          slug: selectedRole.company.slug,
        }
      : {};

  return {
    distinctId: String(user.id),
    personProperties,
    groupType: 'company',
    groupKey: selectedRole ? String(selectedRole.company.id) : undefined,
    groupProperties,
  };
};
